/* eslint-disable react/prop-types */
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, useUnmount } from 'react-use';

import { accountDiscoveryActions, accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getBuildConfig, getLogger, tracker } from 'app-components/utils/core';

import { Connect } from 'connect-web-sdk';

const log = getLogger('FinicityConnect/index.js');

const getChannelFromFinicityEnv = (env) => {
  switch (env) {
    case 'production':
      return 'FINICITY';
    case 'development':
      return 'FINICITY';
    case 'sandbox':
      return 'FINICITY';
    default:
      // TODO: Should throw error here!
      return null;
  }
};

const FinicityConnectWrapper = (props) => {
  const { institution, onClosed, onSuccess, reauthenticate, scope, trackingProperties, fixFiMode } = props;
  const dispatch = useDispatch();

  const [cpSuccessData, setCpSuccessData] = useState({});
  const [showGrowStyles, setShowGrowStyles] = useState(false);
  const destroyedRef = useRef(false);

  const institutionLogin = useSelector((state) => accountDiscoverySelectors.getInstitutionLogin(state, { scope }));
  const finicityEnvOverride = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'finicityEnv'));

  const getFinicityEnv = finicityEnvOverride || getBuildConfig().finicity_env;
  const channel = getChannelFromFinicityEnv(getFinicityEnv);

  const growHeight = institution.name.length <= 20 ? '550px' : '620px';

  const destroyIFrame = () => {
    try {
      Connect.destroy();
    } catch (e) {
      log.error('Error destroying Finicity Connect', e);
    }
  };

  useMount(() => {
    getAggrInfoAndLaunchFinicityConnect();
  });

  useUnmount(() => {
    // Signal that the connection should be destroyed. This covers two cases:
    // 1. Connect was already launched — destroy cleans up the iframe/popup immediately
    // 2. Connect hasn't launched yet (API call in-flight) — the flag prevents launch,
    //    and if launch slips through, onLoad will destroy it
    destroyedRef.current = true;

    destroyIFrame();
  });

  const getAggrInfoAndLaunchFinicityConnect = () => {
    const getAggregationInfoPromise = actionHelpers.bindPromiseAction(
      dispatch,
      accountDiscoveryActions.getAggregationInfo,
    );
    getAggregationInfoPromise(
      {
        channel,
        institution,
        institutionLogin,
        reauthenticate,
      },
      { scope },
    )
      .then(
        (aggregationInfo) => {
          if (destroyedRef.current) return;
          launchFinicityConnect(aggregationInfo);
        },
        () => {
          log.error('Retrieving Aggregation Info Failed');
          // TODO: What to do in case of failure?
        },
      )
      .catch((error) => {
        assert(false, error);
      });
  };

  const handleCancel = (event) => {
    const { code, reason } = event;
    log.info('User cancelled connect', code, reason);

    // using timeout gives time for size transition
    setShowGrowStyles(false);
    setTimeout(() => {
      onClosed({
        trackingProperties: {
          agg_status_code: code,
          agg_status_detail: reason,
        },
      });
    }, 500);
  };

  const handleDone = (event, aggregationInfo) => {
    log.info('User completed connect', event);

    // using timeout gives time for size transition
    setShowGrowStyles(false);
    setTimeout(() => {
      onSuccess({ ...cpSuccessData, channel, pollingRef: aggregationInfo.pollingRef });
    }, 500);
  };

  const handleError = (event) => {
    const { code, reason } = event;
    log.info('Error in connect', code, reason, event);

    const errorTrackingProperties = {
      agg_status_code: code,
      agg_status_detail: reason,
    };
    switch (code) {
      case 500:
        errorTrackingProperties.error_code = 'unknown';
        log.error('Unexpected error when connecting');
        break;
      case 1440:
        errorTrackingProperties.error_code = 'timeout';
        log.error('Timeout when connecting');
        break;
      default:
        errorTrackingProperties.error_code = 'unknown';
        log.error('Unhandled error when connecting', code, reason);
        break;
    }

    tracker.track(!fixFiMode ? tracker.events.addFIError : tracker.events.fixFIError, {
      ...trackingProperties,
      ...errorTrackingProperties,
    });

    // using timeout gives time for size transition
    setShowGrowStyles(false);
    setTimeout(() => {
      onClosed({
        trackingProperties: {
          ...errorTrackingProperties,
        },
      });
    }, 500);
  };

  const handleRoute = (event) => {
    const { screen, params } = event;
    log.info('User navigated in connect', screen, params);
    switch (screen) {
      case 'login': {
        // const { institutionId } = params;
        break;
      }
      case 'mfa': {
        // const { institutionId } = params;
        break;
      }
      case 'done':
        break;
      default:
        break;
    }
  };

  const handleUser = (event) => {
    const { action } = event;
    log.info('User event in connect', action, event);
    switch (action) {
      case 'AddAccounts':
        setCpSuccessData({ ...cpSuccessData, cpId: event.institutionLoginId });
        break;
      case 'AddAccountsSuccess': // Seems to occur twice, OAuthAddAccounts -> AddAccountsSuccess
        break;
      case 'AddAllAccounts':
        break;
      case 'AddAllAccountsSuccess':
        break;
      case 'DisplayAlert': {
        // const { errorCode, message, data } = event;
        break;
      }
      case 'End':
        break;
      case 'FixMfaAttempt':
        break;
      case 'FixSignInAttempt':
        break;
      case 'Initialize': // OAuth & Non-OAuth
        break;
      case 'InitOAuthConnection':
        break;
      case 'LaunchOAuth':
        break;
      case 'MfaAttempt':
        break;
      case 'OAuthAddAccounts': // Seems to occur twice, OAuthAddAccounts -> AddAccountsSuccess
        setCpSuccessData({ ...cpSuccessData, cpId: event.institutionLoginId });
        break;
      case 'OAuthConnectionSuccess':
        break;
      case 'OAuthSignInAttempt':
        break;
      case 'RemoveAccounts':
        // TODO: Says this is supported in connect lite, but how if accounts are not selectable?
        break;
      case 'SignInAttempt': // Non-OAuth only, occurs before and after AddAllAccounts
        break;
      default:
        break;
    }

    if (event.errorCode) {
      const errorTrackingProperties = {
        error_code: event.errorCode,
        agg_status_code: event.errorCode,
      };
      tracker.track(!fixFiMode ? tracker.events.addFIError : tracker.events.fixFIError, {
        ...event,
        ...trackingProperties,
        ...errorTrackingProperties,
      });
    }
  };

  const launchFinicityConnect = (aggregationInfo) => {
    log.info('Launching Finicity Connect', aggregationInfo);
    setShowGrowStyles(true);
    assert(aggregationInfo.connectUrl, 'Finicity connectUrl is missing', aggregationInfo);
    Connect.launch(
      aggregationInfo.connectUrl,
      {
        onCancel: handleCancel,
        onDone: (event) => handleDone(event, aggregationInfo),
        onError: handleError,
        onLoad: () => {
          if (destroyedRef.current) {
            // The iframe has loaded, but the component has already unmounted — destroy to clean up iframe and popup if they exist
            destroyIFrame();
            return;
          }
          log.info('Connect IFrame is loaded!');
        },
        onRoute: handleRoute,
        onUser: handleUser,
      },
      {
        overlay: 'rgba(199,201,199, 0.5)',
        selector: '#add-fi-contents',
      },
    );
  };

  return (
    <div
      id='add-fi-contents'
      style={{
        height: '100px',
        transition: 'height 1s ease',
        ...(showGrowStyles && { height: growHeight }),
      }}
    />
  );
};

export default FinicityConnectWrapper;
